/**
 * The raw voice events behind the transcript, collapsed behind a disclosure rather than
 * dropped — as the eval dashboard keeps its system turns. They are the call's scaffolding, not
 * the consultation, but someone auditing why an exchange reads oddly needs to reach them.
 *
 * The mobile app uploads them when the call ends, so an active session has none yet, and that
 * is said rather than shown as an empty list.
 */

import type { AdminSessionDetail, TranscriptEvent } from "../../api/types";
import { count } from "../../lib/format";

/** An event's time of day to the second, or nothing when it carried none. */
function eventTime(at: string | undefined): string {
  if (!at) return "";
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : date.toLocaleTimeString(undefined, { hour12: false });
}

/** The mapper's matches on an `answer_mapped` event, as "relation: a, b; …". */
function matchedWords(matched: Record<string, string[]>): string {
  return Object.entries(matched)
    .map(([relation, values]) => `${relation}: ${values.join(", ")}`)
    .join("; ");
}

function EventRow({ event }: { event: TranscriptEvent }) {
  const words = event.text ?? event.question ?? "";
  return (
    <li className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-3 text-caption">
      <span className="tabular-nums text-ink-faint">{eventTime(event.at)}</span>
      <span className="whitespace-pre-wrap text-ink-3">
        <span className="text-ink-faint">[{event.type ?? "?"}]</span>
        {event.role ? <span className="text-ink-faint"> {event.role}:</span> : null}
        {words !== "" ? ` ${words}` : null}
        {event.matched_options ? (
          <span className="text-ink-faint"> → {matchedWords(event.matched_options)}</span>
        ) : null}
      </span>
    </li>
  );
}

export default function RawEvents({ detail }: { detail: AdminSessionDetail }) {
  const events = detail.transcript_events;
  if (events.length === 0) {
    return (
      <p className="text-caption text-ink-faint">
        {detail.status === "active"
          ? "The raw voice events upload when the call ends."
          : "No raw voice events were uploaded for this session."}
      </p>
    );
  }
  return (
    <details className="panel px-4 py-2.5 text-body-sm">
      <summary className="cursor-pointer list-none text-ink-2">
        {count(events.length)} raw voice event{events.length === 1 ? "" : "s"} — the call&rsquo;s
        own record, uploaded when it ended
      </summary>
      <ol className="mt-2 flex max-h-96 flex-col gap-1.5 overflow-y-auto">
        {events.map((event, position) => (
          <EventRow key={position} event={event} />
        ))}
      </ol>
    </details>
  );
}
